import { camelize, unref } from 'vue';

import { IN_BROWSER } from './globals';

/**
 * Returns:
 *  - 'null' if the node is not attached to the DOM
 *  - the root node (HTMLDocument | ShadowRoot) otherwise
 */
export function attachedRoot(node: Node): null | HTMLDocument | ShadowRoot {
  /* istanbul ignore next */
  if (typeof node.getRootNode !== 'function') {
    // Shadow DOM not supported (IE11), lets find the root of this node
    while (node.parentNode) node = node.parentNode;

    // The root parent is the document if the node is attached to the DOM
    if (node !== document) return null;

    return document;
  }

  const root = node.getRootNode();

  // The composed root node is the document if the node is attached to the DOM
  if (root !== document && root.getRootNode({ composed: true }) !== document) return null;

  return root as HTMLDocument | ShadowRoot;
}

export function isVisible(element: HTMLElement | SVGGraphicsElement): boolean {
  if (!element) {
    return false;
  }

  if ((element as HTMLElement).offsetParent) {
    return true;
  }

  if ((element as SVGGraphicsElement).getBBox) {
    const box = (element as SVGGraphicsElement).getBBox();
    if (box.width || box.height) {
      return true;
    }
  }

  if ((element as HTMLElement).getBoundingClientRect) {
    const box = (element as HTMLElement).getBoundingClientRect();
    if (box.width || box.height) {
      return true;
    }
  }

  return false;
}

export function isStyleSupport(styleName: string | string[], styleValue?: any) {
  if (!Array.isArray(styleName) && styleValue !== undefined) {
    return isStyleValueSupport(styleName, styleValue);
  }

  return isStyleNameSupport(styleName);
}

export function parseStyleText(cssText = '', camel?: boolean) {
  const res: Record<string, any> = {};
  const listDelimiter = /;(?![^(]*\))/g;
  const propertyDelimiter = /:(.+)/;
  if (typeof cssText === 'object') return cssText;
  cssText.split(listDelimiter).forEach(function (item) {
    if (item) {
      const tmp = item.split(propertyDelimiter);
      if (tmp.length > 1) {
        const k = camel ? camelize(tmp[0].trim()) : tmp[0].trim();
        res[k] = tmp[1].trim();
      }
    }
  });
  return res;
}

export function findDOMNodeByInstance(instance: any) {
  let node = instance?.vnode?.el || (instance && (instance.$el || instance));

  while (node && !node.tagName) {
    node = node.nextSibling;
  }

  return node;
}

export function getElement(el?: HTMLElement | string | null) {
  if (el === undefined || el === null) return undefined;
  if (typeof el === 'string') {
    try {
      return document.querySelector(el) || undefined;
    } catch (err) {
      return undefined;
    }
  }

  const target = unref(el);

  if (target) return (target as any).$el || target;
}

function isStyleNameSupport(styleName: string | string[]) {
  if (IN_BROWSER && window.document.documentElement) {
    const styleNameList = Array.isArray(styleName) ? styleName : [styleName];
    const { documentElement } = window.document;

    return styleNameList.some((name) => name in documentElement.style);
  }

  return false;
}

function isStyleValueSupport(styleName: string | any, value: any) {
  if (!isStyleNameSupport(styleName)) {
    return false;
  }

  const el = document.createElement('div');
  const origin = el.style[styleName];
  el.style[styleName] = value;
  return el.style[styleName] !== origin;
}
